import { createRng } from '../../../core/rng';
import { Grid, hits } from '../song';
import type { Note } from '../song';
import { barsOf, cellsOf, chooser, cycle, find, fitForm, pcs, phrase, titleOf, totalBars, trackId, transpose } from '../score';
import type { Ev, FormPlan, Params, Score, Template } from '../score';

type Chord = { root: string; stab: readonly string[]; pad: readonly string[] };

const CHORDS: Readonly<Record<string, Chord>> = {
  C: { root: 'C2', stab: ['E4', 'G4', 'C5', 'D5'], pad: ['C3', 'G3', 'C4', 'E4'] },
  G: { root: 'G1', stab: ['D4', 'G4', 'B4', 'D5'], pad: ['G2', 'D3', 'B3', 'D4'] },
  Am: { root: 'A1', stab: ['E4', 'A4', 'C5', 'E5'], pad: ['A2', 'E3', 'C4', 'E4'] },
  F: { root: 'F1', stab: ['F4', 'A4', 'C5', 'E5'], pad: ['F2', 'C3', 'A3', 'C4'] },
  Em: { root: 'E2', stab: ['E4', 'G4', 'B4', 'D5'], pad: ['E3', 'B3', 'E4', 'G4'] },
  Dm: { root: 'D2', stab: ['D4', 'F4', 'A4', 'C5'], pad: ['D3', 'A3', 'D4', 'F4'] },
};

const PROGS = [
  ['C', 'G', 'Am', 'F'],
  ['F', 'G', 'Em', 'Am'],
  ['Am', 'F', 'C', 'G'],
  ['C', 'Am', 'F', 'G'],
  ['F', 'C', 'G', 'Am'],
  ['C', 'Em', 'F', 'G'],
] as const;

const HOOK: Note[][] = [
  [[0, 'E5', 2], [2, 'G5', 2], [4, 'C6', 3], [8, 'G5', 1, 0.7], [10, 'A5', 2], [12, 'G5', 2], [14, 'E5', 2]],
  [[0, 'D5', 2], [2, 'G5', 2], [4, 'B5', 3], [8, 'A5', 1, 0.7], [10, 'G5', 2], [12, 'D5', 2], [14, 'B4', 2]],
  [[0, 'C5', 2], [2, 'E5', 2], [4, 'A5', 3], [8, 'G5', 1, 0.7], [10, 'E5', 2], [12, 'C5', 2], [14, 'E5', 2]],
  [[0, 'C5', 2], [2, 'F5', 2], [4, 'A5', 4], [10, 'G5', 2], [12, 'F5', 1, 0.8], [13, 'E5', 1, 0.8], [14, 'D5', 2]],
];

const BASSES: readonly (readonly [step: number, semis: number, len: number][])[] = [
  [[0, 0, 1], [2, 12, 1], [3, 0, 0.8], [6, 12, 1], [8, 0, 1], [10, 12, 1], [11, 7, 0.8], [14, 12, 1.2]],
  [[2, 0, 1.4], [4, 12, 0.8], [6, 0, 1.4], [10, 0, 1.4], [12, 12, 0.8], [14, 7, 1.2]],
  [[0, 0, 1.4], [3, 12, 0.9], [6, 0, 1.4], [8, 7, 0.9], [10, 12, 1], [13, 0, 0.9], [14, 12, 1.2]],
];

const ARPS = [
  [0, 2, 1, 3, 2, 0, 3, 1, 0, 2, 1, 3, 2, 3, 1, 2],
  [0, 1, 2, 3, 1, 2, 3, 0, 2, 3, 0, 1, 3, 2, 1, 0],
  [3, 2, 1, 0, 2, 1, 3, 2, 3, 2, 1, 0, 1, 2, 3, 2],
] as const;

const KEYS = [0, 2, -2, 3, 1, -1, 4] as const;
const CLAPS = ['....X.......X...', '....X.......X..x'] as const;
const CLOSED_HATS = ['x.o.x.o.x.o.x.o.', 'x.oox.o.x.o.x.oo'] as const;
const TAMBS = ['o-o-x-o-o-o-x-o-', 'o-o-x-o-o-oox-o-'] as const;
const STABS = ['X..x..x...x..x..', '..X...X...X...X.', 'X..x..x.x..x..x.'] as const;
const INTRO_STABS = ['..o...o...o...o.', 'o..o..o...o.....'] as const;
const ROLLS = ['X...X...X...X...', 'x.x.x.x.x.x.x.x.', 'xxxxxxxxxxxxxxxx'] as const;
const MAJOR = [0, 2, 4, 5, 7, 9, 11] as const;

const PLAN: FormPlan = {
  intro: 4,
  outro: 4,
  body: [
    { name: 'verse', bars: 8, min: 4, rank: 3 },
    { name: 'build', bars: 2, min: 2, rank: 4 },
    { name: 'drop', bars: 8, min: 4, rank: 1 },
    { name: 'break', bars: 4, min: 2, rank: 5, with: 'drop2' },
    { name: 'drop2', bars: 8, min: 4, rank: 2 },
  ],
};

function score(seed: string, p: Params): Score {
  const ch = chooser(POP, seed);
  const key = ch.pick(KEYS);
  const prog = ch.pick(PROGS).map((n) => CHORDS[n]!);
  const bassRow = ch.pick(BASSES);
  const arpRow = ch.pick(ARPS);
  const clapP = ch.pick(CLAPS);
  const hatCP = ch.pick(CLOSED_HATS);
  const tambP = ch.pick(TAMBS);
  const stabP = ch.pick(STABS);
  const introP = ch.pick(INTRO_STABS);
  const hook = ch.orig
    ? HOOK
    : phrase(
        ch.rng,
        HOOK.map(cellsOf),
        prog.map((c) => [{ at: 0, tones: pcs([c.root, ...c.stab]) }]),
        MAJOR,
        [71, 84],
        76,
      );
  const e = p.energy;
  const tr = (n: string) => transpose(n, key);

  const form = fitForm(barsOf(p), PLAN);
  const g = new Grid(p.bpm, 50, form);
  const s = g.step;
  const intro = form[0]!.bars;
  const total = totalBars(form);
  const outro = total - form[form.length - 1]!.bars;
  const drop1 = find(form, 'drop')!;
  const drop2 = find(form, 'drop2');
  const build = find(form, 'build');
  const verse = find(form, 'verse');
  const rng = createRng(`pop-${seed}`);
  const human = (v: number) => Math.min(1, v * (0.93 + rng.float() * 0.12));

  const events: Ev[] = [];
  const hit = (v: string, bar: number, step: number, vel: number, jitter = 0) =>
    events.push({ v, t: g.at(bar, step) + (jitter === 0 ? 0 : rng.range(-jitter, jitter)), bar, step, vel });
  const play = (v: string, bar: number, step: number, note: string, dur: number, vel: number) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, note });
  const chord = (v: string, bar: number, step: number, notes: readonly string[], dur: number, vel: number) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, notes });

  const autos = [
    { v: 'stabLp', points: [[g.at(0), 700], [g.at(intro), 2000], [g.at(drop1), 5200], [g.at(outro), 5200], [g.at(total), 900]] },
    { v: 'padLp', points: [[g.at(0), 450], [g.at(intro), 1100], [g.at(drop1), 3200], [g.at(outro), 3200], [g.at(total), 400]] },
  ] as Score['autos'];

  for (const b of g.bars()) {
    const i = cycle(b.bar - intro, 4);
    const c = prog[i]!;
    const inDrop = b.name === 'drop' || b.name === 'drop2';
    const inVerse = b.name === 'verse';
    const building = b.name === 'build';
    const inBreak = b.name === 'break';
    const outroBody = b.name === 'outro' && !b.last;
    const introKick = b.name === 'intro' && b.index >= b.bars / 2;
    const band = inDrop || inVerse || outroBody;

    if (band || introKick || (building && !b.last)) {
      for (const h of hits('X...X...X...X...')) hit('kick', b.bar, h.step, h.vel);
    } else if (building) {
      for (const h of hits('X...X...........')) hit('kick', b.bar, h.step, h.vel);
    }
    if (band || building) {
      for (const h of hits(clapP)) hit('clap', b.bar, h.step, human(h.vel));
      if (e >= 0.3) for (const h of hits(hatCP)) hit('hatC', b.bar, h.step, human(h.vel), 0.002);
    }
    if (inVerse || outroBody) for (const h of hits('....x.......x...')) hit('snap', b.bar, h.step, human(h.vel));
    if (inDrop) {
      for (const h of hits('....x.......x...')) hit('snare', b.bar, h.step, human(0.55));
      for (const h of hits('..X...X...X...X.')) hit('hatO', b.bar, h.step, human(0.85));
      if (e >= 0.5) for (const h of hits(tambP)) hit('tamb', b.bar, h.step, human(h.vel), 0.003);
    }
    if (inBreak || b.name === 'intro') for (const h of hits('..o...o...o...o.')) hit('hatC', b.bar, h.step, human(h.vel * 0.8));

    if (band || (building && !b.last)) {
      const row = inVerse && e < 0.6 ? bassRow.filter((_, k) => k % 2 === 0) : bassRow;
      for (const [step, semis, len] of row) {
        const note = tr(transpose(c.root, semis));
        play('bass', b.bar, step, note, len * s * 0.95, human(0.9));
        play('sub', b.bar, step, tr(transpose(c.root, semis === 12 ? 0 : semis)), len * s * 0.95, 0.85);
      }
    }

    const stab = c.stab.map(tr);
    if (b.name === 'intro' || b.name === 'outro') {
      for (const h of hits(introP)) chord('stab', b.bar, h.step, stab, s * 1.6, human(h.vel + 0.2));
    } else if (inVerse || inDrop) {
      for (const h of hits(stabP)) chord('stab', b.bar, h.step, stab, s * 1.4, human(inVerse ? h.vel * 0.8 : h.vel));
    } else if (building) {
      for (const h of hits('x.x.x.x.x.x.x.x.')) chord('stab', b.bar, h.step, stab, s * 1.2, 0.45 + 0.4 * ((b.index * 16 + h.step) / (b.bars * 16)));
    }

    if (b.name === 'intro' || inBreak || building || inDrop || b.name === 'outro') {
      chord('pad', b.bar, 0, c.pad.map(tr), g.barLength * 0.98, inBreak ? 0.9 : 0.6);
    }

    const bells = stab.map((n) => transpose(n, 12));
    if ((inDrop && e >= 0.5) || (inVerse && e >= 0.8)) {
      const k = inDrop ? 1 : 0.6;
      arpRow.forEach((idx, step) => {
        if (step % 2 === 1 && !inDrop) return;
        play('bell', b.bar, step, bells[idx]!, s * 0.8, human((step % 2 === 0 ? 0.75 : 0.45) * k));
      });
    }

    const row = hook[i]!;
    if (inBreak && b.index >= b.bars / 2) {
      for (const [step, note, len, vel] of row) play('bell', b.bar, step, tr(note), len * s * 0.9, (vel ?? 0.9) * 0.7);
    }
    if (inDrop) {
      const octave = (b.name === 'drop2' && e >= 0.6) || e >= 0.85;
      for (const [step, note, len, vel] of row) {
        play('lead', b.bar, step, tr(note), len * s * 0.9, human(vel ?? 0.95));
        if (octave) play('lead', b.bar, step, tr(transpose(note, -12)), len * s * 0.9, (vel ?? 0.9) * 0.5);
      }
    }

    if ((inVerse || inBreak || b.name === 'intro') && b.index % 2 === 1) {
      const n = rng.pick(stab);
      play('bloop', b.bar, 14, transpose(n, 12), s * 1.5, human(0.7));
    }

    if (building) {
      const left = b.bars - 1 - b.index;
      const pattern = ROLLS[left >= 2 ? 0 : left === 1 ? 1 : 2];
      for (const h of hits(pattern)) hit('snare', b.bar, h.step, 0.35 + 0.6 * ((b.index * 16 + h.step) / (b.bars * 16)));
    }
  }

  const riseFrom = build ?? Math.max(0, drop1 - 2);
  if (riseFrom < drop1) events.push({ v: 'inflate', t: g.at(riseFrom), end: g.at(drop1), bar: riseFrom, step: 0, vel: 1 });
  if (drop2 !== undefined) events.push({ v: 'inflate', t: g.at(drop2 - 2), end: g.at(drop2), bar: drop2 - 2, step: 0, vel: 0.85 });
  for (const bar of [drop1, drop2]) {
    if (bar === undefined) continue;
    hit('pop', bar, 0, 1);
    hit('crash', bar, 0, 1);
    hit('impact', bar, 0, 1);
  }
  if (verse !== undefined) hit('crash', verse, 0, 0.7);

  return {
    id: trackId(POP, seed, p),
    template: POP.id,
    seed,
    params: p,
    title: ch.orig ? POP.ref.title : titleOf(seed),
    genre: POP.genre,
    bpm: p.bpm,
    swing: 50,
    lufs: -10.5,
    key,
    form,
    mix: { reverbDecay: 2.2, delayTime: (60 / p.bpm) * 0.75, delayFeedback: 0.28, drive: 1.5, softness: 1.25, pumpDepth: 0.5, pumpRelease: 0.26 },
    events,
    autos,
    accents: { kick: 'kick', clap: 'snare', stab: 'stab', lead: 'melody', impact: 'drop' },
  };
}

export const POP: Template = {
  id: 'pop',
  genre: 'bubblegum pop',
  bpm: [112, 130],
  ref: { seed: 'bala', title: 'Bala', params: { bpm: 120, bars: 28, energy: 0.55 } },
  score,
};
